/**
 * Design System — composition layer
 *
 * Single source of truth for color VALUES is `apps/web/src/client/index.css` (Tailwind v4
 * CSS-first oklch tokens). This module composes those semantic tokens into
 * reusable class strings for business components. Consumers should reach for
 * these consts (or raw semantic classes like `text-foreground`) — never raw
 * Tailwind palette colors (`text-muted-foreground`, `text-success`, …), which are
 * banned by `scripts/check-design-tokens.ts`.
 *
 * Semantic palette (see docs/guides/design-system.md):
 *   surfaces  — bg-background, bg-card, bg-muted, bg-popover
 *   text      — text-foreground, text-foreground/80, text-muted-foreground, text-muted-foreground/70
 *   borders   — border-border, border-input
 *   status    — text-success, text-warning, text-info, text-destructive
 *   money     — credit → text-success, debit → text-destructive (shares hue with status)
 *   categorical — text-chart-1 … text-chart-6 (charts, bureau identity, account-type accents)
 */

// ===== STATUS =====

/**
 * Semantic status colors.
 * success/warning/info/destructive each expose a text/bg/border triplet.
 * `bg` and `border` use opacity tiers so the same hue works on any surface.
 */
export const statusColors = {
  success: {
    text: "text-success",
    bg: "bg-success/10",
    border: "border-success/30",
  },
  warning: {
    text: "text-warning",
    bg: "bg-warning/10",
    border: "border-warning/30",
  },
  error: {
    text: "text-destructive",
    bg: "bg-destructive/10",
    border: "border-destructive/30",
  },
  info: {
    text: "text-info",
    bg: "bg-info/10",
    border: "border-info/30",
  },
} as const;

// ===== TRANSACTIONS =====

/**
 * Transaction type colors and labels.
 * Money direction reuses the status palette: credit → success, debit → destructive.
 * `bgSubtle` is the soft badge background (10% tint).
 */
export const transactionColors = {
  credit: {
    text: "text-success",
    bg: "bg-success/10",
    bgSubtle: "bg-success/10",
    border: "border-success/30",
  },
  debit: {
    text: "text-destructive",
    bg: "bg-destructive/10",
    bgSubtle: "bg-destructive/10",
    border: "border-destructive/30",
  },
  transfer: {
    text: "text-info",
    bg: "bg-info/10",
    bgSubtle: "bg-info/10",
    border: "border-info/30",
  },
} as const;

// ===== ACCOUNT TYPES =====

export const accountTypeLabels = {
  cash: "Cash",
  bank: "Bank Account",
  credit_card: "Credit Card",
  loan: "Loan",
  digital_wallet: "Digital Wallet",
  cryptocurrency: "Cryptocurrency",
  other: "Other",
} as const;
